"use client"

/**
 * ChatScroll Component - CSS Layout + ResizeObserver Scrolling
 *
 * Uses a hybrid approach:
 * - CSS flexbox (justify-end + min-h-full) positions content at bottom
 * - Initial load: scrolls to bottom once
 * - ResizeObserver: detects content size changes, scrolls if user is at bottom
 * - Scroll detection: shows/hides the "scroll to bottom" button
 *
 * REACTIVE: Steps are passed from parent (ChatView) which uses useEditableNodes
 * to merge GraphStore data with optimistic (pending) updates.
 */

import React, { useState, useRef, useEffect } from 'react'
import { ScrollArea } from "@sourcepilot/ui/framework/ui/atoms/scroll-area"

import { Thread } from '@sourcepilot/generated/chat/api/thread';
import { Step } from '@sourcepilot/generated/chat/api/step';
import { Messages } from '@sourcepilot/ui/framework/ui/molecules/messages';
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button';
import { ArrowDown } from 'lucide-react';

// Threshold for considering user "at bottom" (pixels from absolute bottom)
const AT_BOTTOM_THRESHOLD = 50;

interface ChatScrollProps {
  thread: Thread;
  steps: Step[];  // Steps passed from parent (includes optimistic updates)
}

export default function ChatScroll({ thread, steps }: ChatScrollProps) {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);
  const userScrolledAwayRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const needsScrollRef = useRef(false);
  const rafScheduledRef = useRef(false);
  const isAutoScrollingRef = useRef(false);

  // Steps now passed from parent with optimistic updates included

  // Check if currently at bottom of scroll area
  const checkIfAtBottom = (): boolean => {
    const scrollArea = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (!scrollArea) return true;

    const { scrollTop, scrollHeight, clientHeight } = scrollArea;
    return scrollHeight - scrollTop - clientHeight < AT_BOTTOM_THRESHOLD;
  };

  // Scroll to bottom - mark as programmatic to avoid treating scroll event as user scroll.
  //
  // Uses direct viewport.scrollTop = scrollHeight (rather than messagesEndRef.scrollIntoView)
  // so we land precisely at the bottom regardless of trailing padding or anchor offset
  // calculations — relevant when a newly-appended tool-call card is taller than the viewport.
  //
  // Double-RAF defers the scroll past React's commit + the browser's subsequent layout pass,
  // so when a tool-call block is added in multiple phases (header → streamed args → async
  // response) we read the final scrollHeight, not the intermediate one.
  const scrollToBottom = () => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
    if (!viewport) return;
    isAutoScrollingRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        viewport.scrollTop = viewport.scrollHeight;
        lastScrollTopRef.current = viewport.scrollTop;
        // One more frame to absorb the resulting scroll event before clearing the flag.
        requestAnimationFrame(() => {
          isAutoScrollingRef.current = false;
        });
      });
    });
  };

  // Initial scroll to bottom on first load
  useEffect(() => {
    if (isInitialLoadRef.current && steps.length > 0) {
      requestAnimationFrame(() => {
        scrollToBottom();
        isInitialLoadRef.current = false;
      });
    }
  }, [steps.length]);

  // Schedule RAF only when needed (on-demand, not continuous)
  const scheduleScroll = () => {
    // If RAF is already scheduled, just mark that scroll is needed
    if (rafScheduledRef.current) {
      needsScrollRef.current = true;
      return;
    }

    // Mark RAF as scheduled and flag scroll is needed
    rafScheduledRef.current = true;
    needsScrollRef.current = true;

    requestAnimationFrame(() => {
      // Scroll if still needed and user hasn't scrolled away
      if (needsScrollRef.current && !userScrolledAwayRef.current) {
        scrollToBottom();
      }
      needsScrollRef.current = false;
      rafScheduledRef.current = false;
    });
  };

  // ResizeObserver to detect content size changes
  // Schedules RAF only when content changes
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      scheduleScroll();
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Detect scroll position - single source of truth is userScrolledAwayRef
  useEffect(() => {
    const scrollArea = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (!scrollArea) return;

    const handleScroll = () => {
      const currentScrollTop = scrollArea.scrollTop;
      const atBottom = checkIfAtBottom();

      // Ignore scroll events from our own programmatic scrolling
      if (isAutoScrollingRef.current) {
        lastScrollTopRef.current = currentScrollTop;
        return;
      }
      // Flip to "user scrolled away" only on a real upward delta. Without
      // this, any time the messages container grows (new tool-call card,
      // streamed-in response body) `atBottom` momentarily flips false
      // while the user is still — and we'd incorrectly disable auto-scroll.
      const scrolledUp = currentScrollTop < lastScrollTopRef.current;
      if (userScrolledAwayRef.current === false) {
         if (!atBottom && scrolledUp) {
             userScrolledAwayRef.current = true;
             setShowScrollButton(true);
         }
      } else if (atBottom) {
          // User scrolled back to bottom - resume auto-scroll
          userScrolledAwayRef.current = false;
          setShowScrollButton(false);
      }

      lastScrollTopRef.current = currentScrollTop;
    };

    scrollArea.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollArea.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle scroll button click - scroll to bottom and resume auto-scroll
  const handleScrollButtonClick = () => {
    userScrolledAwayRef.current = false;
    isAutoScrollingRef.current = true; // Mark as programmatic scroll
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

    // Use RAF to detect when smooth scroll completes (when we reach bottom)
    const checkScrollComplete = () => {
      if (checkIfAtBottom()) {
        // Scroll completed - we're at bottom
        isAutoScrollingRef.current = false;
        setShowScrollButton(false);
      } else {
        // Still scrolling - check again next frame
        requestAnimationFrame(checkScrollComplete);
      }
    };
    requestAnimationFrame(checkScrollComplete);
  };

  return (
    <div className="flex flex-col h-full relative">
      <ScrollArea
        className="h-full chat-scroll-area"
        ref={scrollAreaRef}
        orientation="vertical"
        scrollHideDelay={0}
      >
        {/* Messages container - natural top-to-bottom layout */}
        <div className="px-[12px] py-4" ref={messagesContainerRef}>
          {/* Messages - reactive, debug/user mode switching handled in Messages component */}
          <div className="space-y-2 text-[13px]">
            <Messages messages={steps} />
            {/* Anchor for button click scroll */}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </ScrollArea>

      {/* scroll-to-bottom button - visible when user scrolls away from bottom */}
      {showScrollButton && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
          <Button
            variant="secondary"
            size="icon"
            className="rounded-full shadow-md w-10 h-10"
            onClick={handleScrollButtonClick}
          >
            <ArrowDown className="h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  );
}
